/**
 * TourShortcuts — keyboard cheat-sheet modal.
 *
 * Opens via the `?` key or the help button in the floating panel. Mounts at
 * the App level inside TourProvider so it works on any route during a tour.
 *
 * Behavior
 *   • Pressing `?` toggles open/close (handled in TourContext).
 *   • Pressing `Esc` while open closes the modal (handled here so it doesn't
 *     conflict with the browser's default Esc-exits-fullscreen).
 *   • Click on the dim layer also closes.
 */
import React, { useEffect } from 'react';
import { useTour } from '../../context/TourContext';
import { Keyboard, X, ChevronLeft, ChevronRight, Play, Maximize, HelpCircle } from 'lucide-react';

const SHORTCUTS = [
  { keys: ['\u2192'],             icon: ChevronRight, label: 'Skip to next scene',     hint: 'Cancels the narrator and jumps forward' },
  { keys: ['\u2190'],             icon: ChevronLeft,  label: 'Previous scene',         hint: 'Jump back one step' },
  { keys: ['Space'],              icon: Play,         label: 'Play / pause',           hint: 'Pause cancels narration; resume re-narrates the scene' },
  { keys: ['F'],                  icon: Maximize,     label: 'Toggle fullscreen',      hint: 'Goes immersive \u2014 perfect for projectors' },
  { keys: ['?'],                  icon: HelpCircle,   label: 'Show / hide this sheet', hint: 'Press Esc or click outside to dismiss' },
];

export default function TourShortcuts() {
  const t = useTour();
  const { shortcutsOpen, toggleShortcuts, isActive } = t;

  // Esc closes the cheat sheet when it's open. We add this listener at the
  // capture phase so it fires before any underlying handlers.
  useEffect(() => {
    if (!shortcutsOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); toggleShortcuts(); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [shortcutsOpen, toggleShortcuts]);

  if (!isActive || !shortcutsOpen) return null;

  return (
    <div
      data-testid="tour-shortcuts-overlay"
      onClick={toggleShortcuts}
      className="fixed inset-0 z-[10000] flex items-center justify-center px-6 animate-fade-in"
      style={{ background: 'rgba(8, 12, 23, 0.72)', backdropFilter: 'blur(6px)' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl bg-slate-900 border border-white/15 shadow-2xl overflow-hidden text-white"
        data-testid="tour-shortcuts-modal"
      >
        <div className="px-5 py-4 border-b border-white/10 flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-sky-500/15 border border-sky-400/30 inline-flex items-center justify-center">
            <Keyboard className="w-4 h-4 text-sky-300" />
          </div>
          <div className="flex-1">
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-sky-300/90 font-semibold">Keyboard shortcuts</div>
            <div className="text-[14px] font-semibold leading-tight">Drive the tour from the keyboard</div>
          </div>
          <button
            data-testid="tour-shortcuts-close"
            onClick={toggleShortcuts}
            aria-label="Close shortcuts"
            className="w-7 h-7 rounded-full border border-white/15 hover:bg-white/10 inline-flex items-center justify-center"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        <ul className="p-4 space-y-1.5">
          {SHORTCUTS.map((sc) => (
            <li
              key={sc.label}
              className="flex items-center gap-3 rounded-xl px-3 py-2.5 bg-white/[0.03] border border-white/5"
            >
              <sc.icon className="w-4 h-4 text-slate-300 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-slate-100">{sc.label}</div>
                <div className="text-[11px] text-slate-400">{sc.hint}</div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {sc.keys.map(k => (
                  <kbd
                    key={k}
                    className="px-2 py-0.5 min-w-[28px] text-center rounded-md bg-slate-800 border border-white/15 text-[11.5px] font-mono font-semibold text-white shadow-[inset_0_-1px_0_rgba(255,255,255,0.05)]"
                  >
                    {k}
                  </kbd>
                ))}
              </div>
            </li>
          ))}
        </ul>

        <div className="px-5 py-3 bg-slate-800/40 border-t border-white/10 text-[11px] text-slate-400 text-center">
          Press <kbd className="px-1.5 py-0.5 rounded bg-slate-700 border border-white/10 text-[10.5px] font-mono text-slate-200">Esc</kbd> or click outside to dismiss
        </div>
      </div>
    </div>
  );
}
